/* eslint-disable no-param-reassign */
import {Box, FormControl, Stack, Typography, useTheme} from '@mui/material';
import {makePostAppointmentAttachment} from 'main/factories/usecases/private/my-appointments';
import {IconIllustration} from 'presentation/components';
import {ALLOWED_EXTENSIONS} from './constants';
import {useUploadFile} from 'presentation/components/UploadFiles/hooks/useUploadFile';
import {useUploadedFilesStore} from 'presentation/components/UploadFiles/store/useUploadedFilesStore';

import {translations} from 'presentation/translations-new';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {AttachmentsProps} from '../types';
import {FileUpload} from './FileUpload';
import {Container, FileInput, Label} from './styles';
import {ModalError} from 'presentation/components/ModalError';
import {HomeEnum} from 'presentation/constants';
import {useNavigate} from 'react-router-dom';
import {SendingFilesSchema} from './fileValidation';

const {advice} = translations['pt-br'].privateFlow.myAppointments.attachments;
const {addAttachments} = translations['pt-br'].others.uploadFiles;
const {tryAgain, goToHome} = translations['pt-br'].commons;
const {
	description,
	headerTitle,
	title: titleModal,
} = translations['pt-br'].privateFlow.requestAuthorizations.errorModal;

export function UploadFiles({
	attendanceId,
	onUploadSuccess,
}: Pick<AttachmentsProps, 'attendanceId' | 'onUploadSuccess'>) {
	const theme = useTheme();
	const navigate = useNavigate();

	const postFactoryAdapter: Parameters<
		typeof useUploadFile
	>[0]['postFactory'] = onUploadProgress => {
		const service = makePostAppointmentAttachment(onUploadProgress);

		return {
			post: params => {
				if (!params.nuControle) {
					return Promise.reject(new Error('nuControle is required'));
				}

				return service.post({
					file: params.file,
					nuControle: params.nuControle,
				});
			},
		};
	};

	const {uploadedFiles, handleUpload, IS_UPLOADING} = useUploadFile({
		postFactory: postFactoryAdapter,
		nuControle: attendanceId,
	});

	const resetUploadedFilesState = useUploadedFilesStore(state => state.resetUploadedFiles);

	const [fileState, setFileState] = useState<File>();
	const [validationError, setValidationError] = useState<string | null>(null);
	const [isOnDropState, setIsOnDropState] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [isUploadTriggered, setIsUploadTriggered] = useState(false);

	const allowedExtensions = ALLOWED_EXTENSIONS.join(', ');

	const uploadedFilesRef = useRef(uploadedFiles);
	uploadedFilesRef.current = uploadedFiles;

	useEffect(() => {
		if (!isUploadTriggered || IS_UPLOADING) return;

		setIsUploadTriggered(false);

		if (uploadedFilesRef.current[0]?.error) {
			setShowErrorModal(true);
			return;
		}

		onUploadSuccess?.();
		resetUploadedFilesState();
		setFileState(undefined);
	}, [IS_UPLOADING, isUploadTriggered]);

	const handleRemoveFile = () => setFileState(undefined);

	function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
		setValidationError(null);
		const {files} = e.target;

		if (!files) {
			return;
		}

		const result = SendingFilesSchema.safeParse(files[0]);

		if (!result.success) {
			setValidationError(result.error.issues[0].message);
			return;
		}

		setFileState(files[0]);
		e.target.value = '';
		setIsOnDropState(false);
	}

	const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setValidationError(null);
		const {files} = event.dataTransfer;

		if (!files) {
			return;
		}

		const result = SendingFilesSchema.safeParse(files[0]);

		if (!result.success) {
			setValidationError(result.error.issues[0].message);
			return;
		}

		setFileState(files[0]);
		setIsOnDropState(false);
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(true);
		setValidationError(null);
	};

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(false);
		setValidationError(null);
	};

	const handleSendFile = () => {
		setValidationError(null);
		if (!fileState) return;
		setIsUploadTriggered(true);
		handleUpload([fileState]);
	};

	const handleRetry = () => {
		setShowErrorModal(false);
		if (fileState) {
			setIsUploadTriggered(true);
			handleUpload([fileState]);
		}
	};

	return (
		<Fragment>
			{fileState ? (
				<Stack
					border={`1px dashed ${theme.colors.gray['40Percent']}`}
					borderRadius={1}
					direction="column"
					gap={2}>
					<FileUpload
						size={fileState.size}
						title={fileState.name}
						loading={IS_UPLOADING}
						progress={uploadedFiles[0]?.progress}
						error={uploadedFiles[0]?.error}
						removeFile={handleRemoveFile}
						sendFile={handleSendFile}
					/>
				</Stack>
			) : (
				<Container
					$isError={!!validationError}
					justifyContent="center"
					alignItems="center"
					sx={{bgcolor: validationError ? 'error' : 'white'}}
					onDrop={handleOnDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					$isOnDrop={isOnDropState}
					spacing={1}>
					<IconIllustration
						customIconName="upload-file-filled"
						variant={validationError ? 'error' : 'primary'}
						size="custom"
					/>
					<FormControl>
						<Label htmlFor="file" as="label">
							{addAttachments}
						</Label>

						<FileInput
							id="file"
							type="file"
							accept={allowedExtensions}
							onChange={e => handleOnChange(e)}
						/>
					</FormControl>

					<Box sx={{textAlign: 'center'}}>
						{validationError ? (
							<Typography variant="body2" color="error">
								{validationError}
							</Typography>
						) : null}

						<Typography
							variant="body2"
							textAlign="center"
							color={validationError ? 'error' : 'text.secondary'}>
							{advice}
						</Typography>
					</Box>
				</Container>
			)}

			<ModalError
				open={showErrorModal}
				onClose={() => setShowErrorModal(false)}
				headerTitle={headerTitle}
				title={titleModal}
				description={description}
				firstButton={{
					title: tryAgain,
					handleClick: handleRetry,
				}}
				secondButton={{
					title: goToHome,
					handleClick: () => navigate(HomeEnum.HOME_PAGE),
				}}
			/>
		</Fragment>
	);
}
